import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  adminsApi,
  InvoiceStatuses,
  ListingsStatuses,
  ListingsTypes,
  TransactionStatuses,
  useGetCountiesQuery,
  useGetCountriesQuery,
  useGetCitiesQuery,
  useGetListingQuery,
  useUpdateListingsMutation,
  useUpdateListingStatusMutation,
} from "api";
import {
  Alert,
  App,
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Form,
  Image,
  Input,
  Modal,
  Progress,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  UserOutlined,
  ShopOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  EyeOutlined,
  HeartOutlined,
  MessageOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  InfoCircleOutlined,
  PlayCircleOutlined,
  FileImageOutlined,
  WarningOutlined,
  LockOutlined,
  MailOutlined,
  GlobalOutlined,
  AimOutlined,
  TagOutlined,
  CreditCardOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { UploadGallery } from "components";
import { SmartSelect } from "components/SmartSelect";
import { dateTimeFormat, displayUserName, format, formErrorHandler, today } from "libs";
import { useAppDispatch, useAppSelector } from "store/hooks";

const { Title, Text, Paragraph } = Typography;

// Status configuration type
interface StatusConfigItem {
  color: string;
  icon: React.ReactNode;
  text: string;
  bgColor: string;
}

interface PaymentStatusConfigItem {
  color: string;
  icon: React.ReactNode;
  text: string;
}

// Helper function to get status config
const getStatusConfig = (status: ListingsStatuses): StatusConfigItem => {
  switch (status) {
    case ListingsStatuses.PENDING:
      return {
        color: "orange",
        icon: <ClockCircleOutlined />,
        text: "În așteptare",
        bgColor: "#fff7e6",
      };
    case ListingsStatuses.ACTIVE:
      return {
        color: "green",
        icon: <CheckCircleOutlined />,
        text: "Activ",
        bgColor: "#f6ffed",
      };
    case ListingsStatuses.INACTIVE:
      return {
        color: "default",
        icon: <CloseCircleOutlined />,
        text: "Inactiv",
        bgColor: "#fafafa",
      };
    case ListingsStatuses.BLOCKED:
      return {
        color: "red",
        icon: <LockOutlined />,
        text: "Blocat",
        bgColor: "#fff2f0",
      };
    case ListingsStatuses.UNPAID:
      return {
        color: "purple",
        icon: <DollarOutlined />,
        text: "Neplătit",
        bgColor: "#f9f0ff",
      };
    default:
      return {
        color: "default",
        icon: <InfoCircleOutlined />,
        text: "Necunoscut",
        bgColor: "#fafafa",
      };
  }
};

// Helper function to get payment status config
const getPaymentStatusConfig = (
  status: TransactionStatuses
): PaymentStatusConfigItem => {
  switch (status) {
    case TransactionStatuses.PENDING:
      return {
        color: "orange",
        icon: <ClockCircleOutlined />,
        text: "În așteptare",
      };
    case TransactionStatuses.PAID:
      return {
        color: "green",
        icon: <CheckCircleOutlined />,
        text: "Plătit",
      };
    case TransactionStatuses.REFUNDED:
      return {
        color: "red",
        icon: <CloseCircleOutlined />,
        text: "Rambursat",
      };
    default:
      return {
        color: "default",
        icon: <InfoCircleOutlined />,
        text: "Necunoscut",
      };
  }
};

// Helper to get listing type label
const getListingTypeLabel = (type: ListingsTypes): string => {
  switch (type) {
    case ListingsTypes.PAID:
      return "Promovat";
    case ListingsTypes.FREE:
      return "Gratuit";
    default:
      return "Necunoscut";
  }
};

export const ListingInfo = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { listingId } = useParams();
  const { notification } = App.useApp();

  const [isEditMode, setIsEditMode] = useState(false);
  const [showAdditionalAgreeModal, setShowAdditionalAgreeModal] = useState<ListingsStatuses | null>(null);
  const [reason, setReason] = useState<string>("");
  const [listingMedia, setListingMedia] = useState<string[]>([]);
  const [changedValues, setChangedValues] = useState<Record<string, unknown>>(
    {}
  );

  const dispatch = useAppDispatch();
  const { TextArea } = Input;

  const { isPostsRead, isPostsDelete, isAll } = useAppSelector(
    (state) => state.loggedAdmin.parsedPermissions
  );

  const { data, isLoading } = useGetListingQuery({ id: Number(listingId) });
  const [
    updateListingMutation,
    { isLoading: isUpdateLoading, isSuccess, error: updateError },
  ] = useUpdateListingsMutation();

  const [
    updateListingStatusMutation,
    {
      isLoading: isUpdateListingStatusLoading,
      isSuccess: isUpdateListingStatusSuccess,
      isError: isUpdateListingStatusError,
    },
  ] = useUpdateListingStatusMutation();

  // Check if admin can manage this post.
  //
  // Paid posts are auto-published (created ACTIVE), so the admin's
  // accept/reject happens AFTER the user pays — accept finalizes the Keez
  // invoice, reject refunds. The previous gate wrongly required the post to
  // be UNPAID/PENDING, which never matches a live paid post. The correct
  // signal is the invoice lifecycle: a paid post is actionable while its
  // invoice is still PROFORMA (payment confirmed, decision pending). Once
  // accepted (FISCAL) or rejected (DELETED) it is settled.
  const canManagePost = (): boolean => {
    if (!data) return false;
    if (data.type === ListingsTypes.FREE) return true;
    if (data.type === ListingsTypes.PAID) {
      return (
        data.payment?.status === TransactionStatuses.PAID &&
        data.payment?.invoiceStatus === InvoiceStatuses.PROFORMA
      );
    }
    return false;
  };

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data,
        dates: data.dates?.map((i) => dayjs(i)),
        country: data.country?.id,
        county: data.county?.id,
        city: data.city?.id,
      });
    }
  }, [data, form]);

  useEffect(() => {
    if (isSuccess || isUpdateListingStatusSuccess) {
      dispatch(adminsApi.util.invalidateTags(["AdminsStats"]));
      notification.success({
        message: "Succes!",
        description: "Anunțul a fost modificat cu succes.",
      });
      setShowAdditionalAgreeModal(null);
      setIsEditMode(false);
      setReason("");
    }
  }, [isSuccess, isUpdateListingStatusSuccess, notification, dispatch]);

  useEffect(() => {
    if (updateError) {
      notification.error({
        message: "Eroare",
        description: "Anunțul nu poate fi modificat.",
      });
      form.setFields(formErrorHandler((updateError as { data: unknown }).data));
      setShowAdditionalAgreeModal(null);
    }
  }, [updateError, notification, form]);

  useEffect(() => {
    if (isUpdateListingStatusError) {
      notification.error({
        message: "Eroare",
        description: "Statusul anunțului nu poate fi modificat.",
      });
    }
  }, [isUpdateListingStatusError, notification]);

  const normFile = (e: unknown) => {
    if (Array.isArray(e)) return e;
    return (e as { fileList?: unknown[] })?.fileList;
  };

  const onFormSubmit = () => {
    updateListingMutation({
      id: data?.id,
      ...changedValues,
      mediaFiles: [
        ...(data?.mediaFiles.map((i) => i.id) || []),
        ...listingMedia,
      ],
      dates: (changedValues.dates as string[] | undefined)?.map((i: string) =>
        format(i, "YYYY-MM-DD")
      ),
    });
  };

  const onValuesChange = (changed: Record<string, unknown>) => {
    setChangedValues((prev) => ({ ...prev, ...changed }));
  };

  const validateAndSubmit = () => {
    form
      .validateFields()
      .then(() => onFormSubmit())
      .catch((e) => console.log(e, "submit error"));
  };

  const getStatusTag = (status: ListingsStatuses) => {
    const config = getStatusConfig(status);
    return (
      <Tag
        color={config.color}
        icon={config.icon}
        style={{ padding: "4px 12px", fontSize: 13 }}
      >
        {config.text}
      </Tag>
    );
  };

  const getPaymentStatusTag = (status: TransactionStatuses) => {
    const config = getPaymentStatusConfig(status);
    return (
      <Tag
        color={config.color}
        icon={config.icon}
        style={{ padding: "4px 12px", fontSize: 13 }}
      >
        {config.text}
      </Tag>
    );
  };

  const handleStatusChange = () => {
    if (showAdditionalAgreeModal !== null && data?.id) {
      updateListingStatusMutation({
        id: data.id,
        status: showAdditionalAgreeModal,
        reason: reason || undefined,
      });
    }
  };

  const handleCloseModal = () => {
    setShowAdditionalAgreeModal(null);
    setReason("");
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div style={{ padding: "24px" }}>
        <Card>
          <Skeleton active avatar paragraph={{ rows: 4 }} />
        </Card>
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24} lg={16}>
            <Card>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card>
              <Skeleton active paragraph={{ rows: 4 }} />
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        style={{
          padding: "48px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <Empty
          description="Anunțul nu a fost găsit"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  // A paid post that's been paid and is still PROFORMA awaits the admin's
  // accept (→ generate Keez fiscal invoice) or reject (→ refund) decision.
  // The post itself is already ACTIVE, so the status-based flags below don't
  // cover it on their own.
  const isPaidAwaitingDecision =
    data.type === ListingsTypes.PAID &&
    data.payment?.status === TransactionStatuses.PAID &&
    data.payment?.invoiceStatus === InvoiceStatuses.PROFORMA;

  const canShowActivateButton =
    data.status === ListingsStatuses.PENDING ||
    data.status === ListingsStatuses.BLOCKED ||
    data.status === ListingsStatuses.UNPAID ||
    isPaidAwaitingDecision;

  const canShowRejectButton =
    data.status === ListingsStatuses.PENDING ||
    data.status === ListingsStatuses.ACTIVE ||
    data.status === ListingsStatuses.UNPAID;

  const getActivateButtonText = (): string => {
    // For a paid post the activate action means "accept & issue the invoice".
    if (isPaidAwaitingDecision) {
      return "Acceptă";
    }
    return "Activează";
  };

  const getRejectButtonText = (): string => {
    // A paid post in decision is rejected → refund (not a plain deactivate).
    if (isPaidAwaitingDecision) {
      return "Respinge";
    }
    if (data.status === ListingsStatuses.ACTIVE) {
      return "Dezactivează";
    }
    return "Respinge";
  };

  // Listing types for select
  const listingTypesOptions = [
    { value: ListingsTypes.FREE, label: "Gratuit" },
    { value: ListingsTypes.PAID, label: "Promovat" },
  ];

  return (
    <div style={{ padding: "16px", maxWidth: 1600, margin: "0 auto" }}>
      <Form
        form={form}
        onValuesChange={onValuesChange}
        requiredMark={false}
        layout="vertical"
      >
        {/* Header Section */}
        <Card
          style={{
            marginBottom: 24,
            borderRadius: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
          styles={{ body: { padding: "20px 24px" } }}
        >
          <Row gutter={[24, 16]} align="middle" justify="space-between">
            <Col xs={24} md={16}>
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <Space wrap align="center">
                  <Title level={4} style={{ margin: 0 }}>
                    {data.title || "Fără titlu"}
                  </Title>
                  {getStatusTag(data.status)}
                  {data.isExpired && (
                    <Tag color="error" icon={<WarningOutlined />}>
                      Expirat
                    </Tag>
                  )}
                  <Tag
                    color={data.type === ListingsTypes.PAID ? "gold" : "blue"}
                    icon={
                      data.type === ListingsTypes.PAID ? (
                        <DollarOutlined />
                      ) : (
                        <TagOutlined />
                      )
                    }
                  >
                    {getListingTypeLabel(data.type)}
                  </Tag>
                </Space>
                <Space split={<Divider type="vertical" />} wrap>
                  <Text type="secondary">
                    <CalendarOutlined style={{ marginRight: 6 }} />
                    Creat: {format(data.createdAt, dateTimeFormat)}
                  </Text>
                  {data.category?.name && (
                    <Text type="secondary">
                      <TagOutlined style={{ marginRight: 6 }} />
                      {data.category.name}
                    </Text>
                  )}
                  <Text type="secondary">ID: #{data.id}</Text>
                </Space>
              </Space>
            </Col>

            <Col xs={24} md={8}>
              <Space
                wrap
                style={{ width: "100%", justifyContent: "flex-end" }}
              >
                {!canManagePost() && data.type === ListingsTypes.PAID && (
                  <Tooltip
                    title={
                      data.payment?.status !== TransactionStatuses.PAID
                        ? "Postarea plătită poate fi gestionată doar după confirmarea plății"
                        : "Această postare plătită a fost deja procesată (factură emisă sau plată returnată)"
                    }
                  >
                    <Tag
                      color="warning"
                      icon={<InfoCircleOutlined />}
                      style={{ padding: "4px 8px" }}
                    >
                      Acțiuni restricționate
                    </Tag>
                  </Tooltip>
                )}

                {(isAll || isPostsRead) && canManagePost() && (
                  <>
                    {isEditMode ? (
                      <>
                        <Button onClick={() => setIsEditMode(false)}>
                          Anulează
                        </Button>
                        <Button
                          type="primary"
                          icon={<CheckCircleOutlined />}
                          onClick={validateAndSubmit}
                          loading={isUpdateLoading}
                        >
                          Salvează
                        </Button>
                      </>
                    ) : (
                      <Button
                        type="default"
                        icon={<EditOutlined />}
                        onClick={() => setIsEditMode(true)}
                      >
                        Editează
                      </Button>
                    )}

                    {canShowActivateButton && (
                      <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={() =>
                          setShowAdditionalAgreeModal(ListingsStatuses.ACTIVE)
                        }
                        style={{
                          background: "#52c41a",
                          borderColor: "#52c41a",
                        }}
                      >
                        {getActivateButtonText()}
                      </Button>
                    )}
                  </>
                )}

                {(isAll || isPostsDelete) &&
                  canManagePost() &&
                  canShowRejectButton && (
                    <Button
                      danger
                      icon={<CloseCircleOutlined />}
                      onClick={() =>
                        setShowAdditionalAgreeModal(ListingsStatuses.BLOCKED)
                      }
                    >
                      {getRejectButtonText()}
                    </Button>
                  )}
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Rejection Reason Alert */}
        {data.reason && (
          <Alert
            message="Motiv respingere"
            description={data.reason}
            type="error"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginBottom: 24, borderRadius: 8 }}
          />
        )}

        <Row gutter={[24, 24]}>
          {/* Left Column - Main Content */}
          <Col xs={24} xl={16}>
            {/* Post Content Card */}
            <Card
              title={
                <Space>
                  <FileImageOutlined />
                  <span>Conținut anunț</span>
                </Space>
              }
              style={{
                marginBottom: 24,
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
              styles={{ header: { borderBottom: "1px solid #f0f0f0" } }}
            >
              {/* Title */}
              <div style={{ marginBottom: 24 }}>
                <Text
                  strong
                  style={{
                    display: "block",
                    marginBottom: 8,
                    color: "#666",
                    fontSize: 13,
                  }}
                >
                  TITLU
                </Text>
                {isEditMode ? (
                  <Form.Item
                    name="title"
                    rules={[
                      { required: true, message: "Titlul este obligatoriu" },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      placeholder="Introdu titlul"
                      size="large"
                      style={{ borderRadius: 8 }}
                    />
                  </Form.Item>
                ) : (
                  <Title level={5} style={{ margin: 0 }}>
                    {data.title}
                  </Title>
                )}
              </div>

              {/* Description */}
              <div style={{ marginBottom: 24 }}>
                <Text
                  strong
                  style={{
                    display: "block",
                    marginBottom: 8,
                    color: "#666",
                    fontSize: 13,
                  }}
                >
                  DESCRIERE
                </Text>
                {isEditMode ? (
                  <Form.Item
                    name="description"
                    rules={[
                      {
                        required: true,
                        message: "Descrierea este obligatorie",
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <TextArea
                      rows={5}
                      placeholder="Introdu descrierea"
                      style={{ borderRadius: 8 }}
                    />
                  </Form.Item>
                ) : (
                  <Paragraph
                    style={{
                      margin: 0,
                      fontSize: 14,
                      lineHeight: 1.8,
                      color: "#333",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {data.description || "Fără descriere"}
                  </Paragraph>
                )}
              </div>

              {/* Type */}
              <div style={{ marginBottom: 24 }}>
                <Text
                  strong
                  style={{
                    display: "block",
                    marginBottom: 8,
                    color: "#666",
                    fontSize: 13,
                  }}
                >
                  TIP ANUNȚ
                </Text>
                {isEditMode ? (
                  <Form.Item name="type" style={{ marginBottom: 0 }}>
                    <Select
                      style={{ width: 200 }}
                      placeholder="Alege tipul"
                      size="large"
                      options={listingTypesOptions}
                    />
                  </Form.Item>
                ) : (
                  <Tag
                    color={data.type === ListingsTypes.PAID ? "gold" : "blue"}
                    icon={
                      data.type === ListingsTypes.PAID ? (
                        <DollarOutlined />
                      ) : (
                        <TagOutlined />
                      )
                    }
                    style={{ padding: "6px 16px", fontSize: 14 }}
                  >
                    {getListingTypeLabel(data.type)}
                  </Tag>
                )}
              </div>

              {/* Dates */}
              <div style={{ marginBottom: 24 }}>
                <Text
                  strong
                  style={{
                    display: "block",
                    marginBottom: 8,
                    color: "#666",
                    fontSize: 13,
                  }}
                >
                  DATE PUBLICARE
                </Text>
                {isEditMode ? (
                  <Form.Item name="dates" style={{ marginBottom: 0 }}>
                    <DatePicker
                      multiple
                      minDate={today}
                      size="large"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                ) : (
                  <Space wrap>
                    {data.dates && data.dates.length > 0 ? (
                      data.dates.map((date, index) => (
                        <Tag
                          key={index}
                          icon={<CalendarOutlined />}
                          style={{
                            padding: "4px 12px",
                            borderRadius: 6,
                            background: "#f5f5f5",
                            border: "none",
                          }}
                        >
                          {format(date)}
                        </Tag>
                      ))
                    ) : (
                      <Text type="secondary">Fără date specificate</Text>
                    )}
                  </Space>
                )}
              </div>

              {/* Media Files */}
              <div>
                <Text
                  strong
                  style={{
                    display: "block",
                    marginBottom: 12,
                    color: "#666",
                    fontSize: 13,
                  }}
                >
                  MEDIA ({data.mediaFiles?.length || 0})
                </Text>
                {isEditMode ? (
                  <Form.Item
                    name="mediaFiles"
                    valuePropName="fileList"
                    getValueFromEvent={normFile}
                    style={{ marginBottom: 0 }}
                  >
                    <UploadGallery
                      customRequest={() => null}
                      onImagesChange={setListingMedia}
                    />
                  </Form.Item>
                ) : data.mediaFiles && data.mediaFiles.length > 0 ? (
                  <Image.PreviewGroup>
                    <Row gutter={[12, 12]}>
                      {data.mediaFiles.map((file, index) => (
                        <Col key={file.id || index} xs={12} sm={8} md={6}>
                          <div
                            style={{
                              position: "relative",
                              borderRadius: 8,
                              overflow: "hidden",
                              aspectRatio: "1",
                              background: "#f5f5f5",
                            }}
                          >
                            {file.mediaType === 2 ? (
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  background: "#000",
                                }}
                              >
                                <video
                                  src={file.url || ""}
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                  }}
                                  controls
                                />
                              </div>
                            ) : (
                              <Image
                                src={file.url || ""}
                                alt={`Media ${index + 1}`}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                                placeholder={
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Skeleton.Image active />
                                  </div>
                                }
                              />
                            )}
                            {file.mediaType === 2 && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: 8,
                                  right: 8,
                                  background: "rgba(0,0,0,0.6)",
                                  borderRadius: 4,
                                  padding: "2px 6px",
                                }}
                              >
                                <PlayCircleOutlined
                                  style={{ color: "#fff", fontSize: 12 }}
                                />
                              </div>
                            )}
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Image.PreviewGroup>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Fără fișiere media"
                    style={{ margin: "20px 0" }}
                  />
                )}
              </div>
            </Card>

            {/* Location Card */}
            <Card
              title={
                <Space>
                  <EnvironmentOutlined />
                  <span>Locație</span>
                </Space>
              }
              style={{
                marginBottom: 24,
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
              styles={{ header: { borderBottom: "1px solid #f0f0f0" } }}
            >
              <Row gutter={[24, 24]}>
                <Col xs={24} sm={8}>
                  <Text
                    strong
                    style={{
                      display: "block",
                      marginBottom: 8,
                      color: "#666",
                      fontSize: 13,
                    }}
                  >
                    <GlobalOutlined style={{ marginRight: 6 }} />
                    ȚARĂ
                  </Text>
                  {isEditMode ? (
                    <Form.Item name="country" style={{ marginBottom: 0 }}>
                      <SmartSelect
                        query={useGetCountriesQuery}
                        placeholder="Alege țara"
                        allowClear
                        size="large"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  ) : (
                    <Text style={{ fontSize: 15 }}>
                      {data.country?.name || "-"}
                    </Text>
                  )}
                </Col>

                <Col xs={24} sm={8}>
                  <Text
                    strong
                    style={{
                      display: "block",
                      marginBottom: 8,
                      color: "#666",
                      fontSize: 13,
                    }}
                  >
                    <EnvironmentOutlined style={{ marginRight: 6 }} />
                    JUDEȚ
                  </Text>
                  {isEditMode ? (
                    <Form.Item name="county" style={{ marginBottom: 0 }}>
                      <SmartSelect
                        query={useGetCountiesQuery}
                        placeholder="Alege județul"
                        allowClear
                        size="large"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  ) : (
                    <Text style={{ fontSize: 15 }}>
                      {data.county?.name || "-"}
                    </Text>
                  )}
                </Col>

                <Col xs={24} sm={8}>
                  <Text
                    strong
                    style={{
                      display: "block",
                      marginBottom: 8,
                      color: "#666",
                      fontSize: 13,
                    }}
                  >
                    <AimOutlined style={{ marginRight: 6 }} />
                    ORAȘ
                  </Text>
                  {isEditMode ? (
                    <Form.Item name="city" style={{ marginBottom: 0 }}>
                      <SmartSelect
                        query={useGetCitiesQuery}
                        placeholder="Alege orașul"
                        allowClear
                        size="large"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  ) : (
                    <Text style={{ fontSize: 15 }}>
                      {data.city?.name || "-"}
                    </Text>
                  )}
                </Col>
              </Row>

              {data.latitude && data.longitude && (
                <div
                  style={{
                    marginTop: 20,
                    paddingTop: 20,
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    <AimOutlined style={{ marginRight: 6 }} />
                    Coordonate: {data.latitude}, {data.longitude}
                  </Text>
                </div>
              )}
            </Card>
          </Col>

          {/* Right Column - Sidebar */}
          <Col xs={24} xl={8}>
            {/* Statistics Card */}
            <Card
              title={
                <Space>
                  <EyeOutlined />
                  <span>Statistici</span>
                </Space>
              }
              style={{
                marginBottom: 24,
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
              styles={{ header: { borderBottom: "1px solid #f0f0f0" } }}
            >
              <Row gutter={[16, 24]}>
                <Col span={12}>
                  <Statistic
                    title={
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Vizualizări
                      </Text>
                    }
                    value={data.views || 0}
                    prefix={<EyeOutlined style={{ color: "#1890ff" }} />}
                    valueStyle={{ color: "#1890ff", fontSize: 24 }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title={
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Like-uri
                      </Text>
                    }
                    value={data.likes || 0}
                    prefix={<HeartOutlined style={{ color: "#eb2f96" }} />}
                    valueStyle={{ color: "#eb2f96", fontSize: 24 }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title={
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Click-uri
                      </Text>
                    }
                    value={data.clicked || 0}
                    prefix={<EyeOutlined style={{ color: "#722ed1" }} />}
                    valueStyle={{ color: "#722ed1", fontSize: 24 }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title={
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Comentarii
                      </Text>
                    }
                    value={data.comments || 0}
                    prefix={<MessageOutlined style={{ color: "#52c41a" }} />}
                    valueStyle={{ color: "#52c41a", fontSize: 24 }}
                  />
                </Col>
              </Row>

              {/* Engagement Rate */}
              <Divider style={{ margin: "20px 0 16px" }} />
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 8,
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Rată de engagement
                  </Text>
                  <Text strong style={{ fontSize: 12 }}>
                    {data.views
                      ? (
                          (((data.likes || 0) +
                            (data.comments || 0) +
                            (data.clicked || 0)) /
                            data.views) *
                          100
                        ).toFixed(1)
                      : 0}
                    %
                  </Text>
                </div>
                <Progress
                  percent={
                    data.views
                      ? Math.min(
                          (((data.likes || 0) +
                            (data.comments || 0) +
                            (data.clicked || 0)) /
                            data.views) *
                            100,
                          100
                        )
                      : 0
                  }
                  showInfo={false}
                  strokeColor={{
                    "0%": "#1890ff",
                    "100%": "#52c41a",
                  }}
                  size="small"
                />
              </div>
            </Card>

            {/* User Card */}
            <Card
              title={
                <Space>
                  <UserOutlined />
                  <span>Utilizator</span>
                </Space>
              }
              style={{
                marginBottom: 24,
                borderRadius: 12,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
              styles={{ header: { borderBottom: "1px solid #f0f0f0" } }}
            >
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <Avatar
                  size={80}
                  src={data.user?.profilePicture}
                  icon={<UserOutlined />}
                  style={{
                    border: "3px solid #f0f0f0",
                    marginBottom: 12,
                  }}
                />
                <Title level={5} style={{ margin: 0 }}>
                  {displayUserName(data.user)}
                </Title>
              </div>

              <Divider style={{ margin: "16px 0" }} />

              <Space direction="vertical" style={{ width: "100%" }} size={12}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <MailOutlined style={{ color: "#999", fontSize: 14 }} />
                  <Text
                    style={{ fontSize: 13 }}
                    copyable={{ tooltips: ["Copiază", "Copiat!"] }}
                  >
                    {data.user?.email}
                  </Text>
                </div>

                {data.user?.lastActivity && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <HistoryOutlined style={{ color: "#999", fontSize: 14 }} />
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      Ultima activitate:{" "}
                      {format(data.user.lastActivity, dateTimeFormat)}
                    </Text>
                  </div>
                )}
              </Space>

              {/* User Bio */}
              {data.user?.description && (
                <>
                  <Divider style={{ margin: "16px 0" }} />
                  <div>
                    <Text
                      strong
                      style={{
                        display: "block",
                        marginBottom: 8,
                        color: "#666",
                        fontSize: 12,
                      }}
                    >
                      BIO
                    </Text>
                    <Paragraph
                      ellipsis={{
                        rows: 3,
                        expandable: true,
                        symbol: "Mai mult",
                      }}
                      style={{
                        margin: 0,
                        fontSize: 13,
                        color: "#555",
                        lineHeight: 1.6,
                      }}
                    >
                      {data.user.description}
                    </Paragraph>
                  </div>
                </>
              )}
            </Card>

            {/* Company Card */}
            {data.company && (
              <Card
                title={
                  <Space>
                    <ShopOutlined />
                    <span>Companie</span>
                  </Space>
                }
                style={{
                  marginBottom: 24,
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
                styles={{ header: { borderBottom: "1px solid #f0f0f0" } }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                  }}
                >
                  <Avatar
                    size={64}
                    src={data.company.logo}
                    icon={<ShopOutlined />}
                    shape="square"
                    style={{
                      borderRadius: 8,
                      border: "1px solid #f0f0f0",
                    }}
                  />
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      {data.company.name}
                    </Title>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      ID: #{data.company.id}
                    </Text>
                  </div>
                </div>
              </Card>
            )}

            {/* Payment Card */}
            {data.type === ListingsTypes.PAID && data.payment && (
              <Card
                title={
                  <Space>
                    <CreditCardOutlined />
                    <span>Informații plată</span>
                  </Space>
                }
                style={{
                  marginBottom: 24,
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  background:
                    data.payment.status === TransactionStatuses.PAID
                      ? "#f6ffed"
                      : data.payment.status === TransactionStatuses.PENDING
                      ? "#fffbe6"
                      : "#fff",
                }}
                styles={{ header: { borderBottom: "1px solid #f0f0f0" } }}
              >
                <Space direction="vertical" style={{ width: "100%" }} size={16}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Text type="secondary">Status</Text>
                    {getPaymentStatusTag(data.payment.status)}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Text type="secondary">Data</Text>
                    <Text>
                      {format(data.payment.createdAt, dateTimeFormat)}
                    </Text>
                  </div>

                  {data.payment.id && (
                    <div>
                      <Text
                        type="secondary"
                        style={{ display: "block", marginBottom: 4 }}
                      >
                        ID Tranzacție
                      </Text>
                      <Text
                        code
                        copyable
                        style={{
                          fontSize: 11,
                          wordBreak: "break-all",
                          display: "block",
                          padding: "8px",
                          background: "#fafafa",
                          borderRadius: 4,
                        }}
                      >
                        {data.payment.id}
                      </Text>
                    </div>
                  )}
                </Space>
              </Card>
            )}

            {/* Category Card */}
            {data.category?.id && (
              <Card
                title={
                  <Space>
                    <TagOutlined />
                    <span>Categorie</span>
                  </Space>
                }
                style={{
                  marginBottom: 24,
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
                styles={{
                  header: { borderBottom: "1px solid #f0f0f0" },
                  body: { padding: "16px 24px" },
                }}
              >
                <Tag
                  color="purple"
                  style={{ padding: "6px 16px", fontSize: 14 }}
                >
                  {data.category.name}
                </Tag>
              </Card>
            )}
          </Col>
        </Row>
      </Form>

      {/* Status Change Modal */}
      <Modal
        open={showAdditionalAgreeModal !== null}
        title={
          <Space>
            {showAdditionalAgreeModal === ListingsStatuses.ACTIVE ? (
              <CheckCircleOutlined style={{ color: "#52c41a" }} />
            ) : (
              <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
            )}
            <span>Confirmare modificare status</span>
          </Space>
        }
        okText={
          showAdditionalAgreeModal === ListingsStatuses.ACTIVE
            ? getActivateButtonText()
            : getRejectButtonText()
        }
        cancelText="Anulează"
        onCancel={handleCloseModal}
        onOk={handleStatusChange}
        okButtonProps={{
          loading: isUpdateListingStatusLoading,
          disabled:
            showAdditionalAgreeModal === ListingsStatuses.BLOCKED &&
            !reason.trim(),
          danger: showAdditionalAgreeModal === ListingsStatuses.BLOCKED,
          style:
            showAdditionalAgreeModal === ListingsStatuses.ACTIVE
              ? { background: "#52c41a", borderColor: "#52c41a" }
              : {},
        }}
        centered
        width={480}
      >
        <div style={{ padding: "20px 0" }}>
          <Alert
            message={
              showAdditionalAgreeModal === ListingsStatuses.ACTIVE
                ? isPaidAwaitingDecision
                  ? "Postarea va fi acceptată și se va emite factura fiscală în Keez."
                  : "Anunțul va fi activat și vizibil pentru toți utilizatorii."
                : isPaidAwaitingDecision
                  ? "Postarea va fi respinsă, iar plata va fi returnată utilizatorului."
                  : "Anunțul va fi blocat și nu va mai fi vizibil."
            }
            type={
              showAdditionalAgreeModal === ListingsStatuses.ACTIVE
                ? "success"
                : "warning"
            }
            showIcon
            style={{ marginBottom: 20 }}
          />

          {showAdditionalAgreeModal === ListingsStatuses.BLOCKED && (
            <div>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                Motivul respingerii *
              </Text>
              <TextArea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Introdu motivul respingerii..."
                rows={3}
                style={{ borderRadius: 8 }}
              />
              <Text
                type="secondary"
                style={{ fontSize: 12, marginTop: 8, display: "block" }}
              >
                Acest mesaj va fi vizibil pentru utilizator.
              </Text>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};